// Pause Rules Engine — recurring rules (M-F, weekends, nights) + manual pause,
// timezone-aware, handles cross-midnight ranges.
//
// ── THE RULES ARE PASSED IN, NOT READ FROM STORAGE ────────────────────────────
// This engine used to call getPauseRules(restaurantId), which read localStorage. The
// kitchen tablet wrote that key; the GUEST'S PHONE never had it. GuestMenu called the
// same function and therefore always got "not paused" — so a restaurant that closed,
// or restricted itself to desserts after 10pm, was invisible to guests. They saw the
// full menu and could order anything. The feature had never worked anywhere except
// the tablet that set it.
//
// Rules now belong to the RESTAURANT and come from the server: the guest receives
// them on the same GET /menu call that returns the food. This engine is pure — give
// it rules, it evaluates them — so it cannot silently evaluate an empty set again.

/**
 * @param {object} rules - { recurringRules: [], manualPause: {} } from the server
 * @param {string} timezone
 * @returns {object} { paused, availableCategories, reason, ruleId }
 */
export function evaluatePauseState(rules, timezone = 'AEST') {
  if (!rules || (rules.length === 0 && !rules.manualPause?.active)) {
    return { paused: false, availableCategories: [], reason: null, ruleId: null };
  }

  const now = new Date();
  const tzNow = convertToTz(now, timezone);
  const dayOfWeek = getDayOfWeek(tzNow);
  const timeStr = formatTimeHHMM(tzNow);
  const dateStr = formatDateYYYYMMDD(tzNow);

  // Priority 1: Manual pause (highest priority)
  if (rules.manualPause?.active && rules.manualPause?.until) {
    const until = new Date(rules.manualPause.until);
    if (now < until) {
      return {
        paused: true,
        availableCategories: rules.manualPause.availableCategories || [],
        reason: 'Manual pause',
        ruleId: 'manual',
        until: rules.manualPause.until,
      };
    }
  }

  // Priority 2: Exception rules (holidays, special dates)
  const exceptions = (rules.recurringRules || []).filter((r) => r.type === 'exception' && r.active);
  for (const rule of exceptions.sort((a, b) => (a.priority || 999) - (b.priority || 999))) {
    if (rule.dates?.includes(dateStr)) {
      return {
        paused: true,
        availableCategories: rule.availableCategories || [],
        reason: rule.name,
        ruleId: rule.id,
      };
    }
  }

  // Priority 3: Recurring rules (M-F, weekends, nights)
  const recurring = (rules.recurringRules || []).filter((r) => !r.type && r.active);
  for (const rule of recurring.sort((a, b) => (a.priority || 999) - (b.priority || 999))) {
    if (rule.daysOfWeek?.includes(dayOfWeek) && timeInRange(timeStr, rule.timeRange)) {
      return {
        paused: true,
        availableCategories: rule.availableCategories || [],
        reason: rule.name,
        ruleId: rule.id,
      };
    }
  }

  // No rules matched
  return { paused: false, availableCategories: [], reason: null, ruleId: null };
}

/**
 * LOCAL CACHE ONLY — never the source of truth.
 *
 * Kept so the kitchen still shows the right state on a cold start before the API
 * answers, and keeps working through a brief network drop. Keyed by restaurant,
 * because the rules are the restaurant's.
 */
export function getPauseRules(restaurantId) {
  try {
    const key = `gapgo_pause_rules_${restaurantId}`;
    const data = localStorage.getItem(key);
    if (!data) return { recurringRules: [], manualPause: { active: false, until: null } };
    return JSON.parse(data);
  } catch {
    return { recurringRules: [], manualPause: { active: false, until: null } };
  }
}

/**
 * Save pause rules to localStorage
 * @param {string} restaurantId
 * @param {object} rules - { recurringRules: [...], manualPause: {...} }
 */
export function setPauseRules(restaurantId, rules) {
  try {
    const key = `gapgo_pause_rules_${restaurantId}`;
    localStorage.setItem(key, JSON.stringify(rules));
  } catch (e) {
    console.error('Failed to save pause rules:', e);
  }
}

/**
 * Add or update a recurring rule
 * @param {string} restaurantId
 * @param {object} rule - { id, name, active, daysOfWeek, timeRange, availableCategories, priority }
 */
export function addRecurringRule(restaurantId, rule) {
  const rules = getPauseRules(restaurantId);
  const idx = (rules.recurringRules || []).findIndex((r) => r.id === rule.id);
  if (idx >= 0) {
    rules.recurringRules[idx] = rule;
  } else {
    if (!rules.recurringRules) rules.recurringRules = [];
    rules.recurringRules.push(rule);
  }
  setPauseRules(restaurantId, rules);
}

/**
 * Delete a recurring rule
 * @param {string} restaurantId
 * @param {string} ruleId
 */
export function deleteRecurringRule(restaurantId, ruleId) {
  const rules = getPauseRules(restaurantId);
  rules.recurringRules = (rules.recurringRules || []).filter((r) => r.id !== ruleId);
  setPauseRules(restaurantId, rules);
}

/**
 * Set manual pause until a specific time
 * @param {string} restaurantId
 * @param {string|null} until - ISO timestamp or null to cancel
 * @param {array} availableCategories - Cats available while paused
 */
export function setManualPause(restaurantId, until, availableCategories = []) {
  const rules = getPauseRules(restaurantId);
  rules.manualPause = {
    active: !!until,
    until,
    availableCategories,
  };
  setPauseRules(restaurantId, rules);
}

// ─── HELPERS ───

function convertToTz(date, timezone) {
  const TZ_OFFSETS = {
    AEST: 10 * 60,   // +10:00
    AEDT: 11 * 60,   // +11:00
    ACST: 9.5 * 60,  // +09:30
    ACDT: 10.5 * 60, // +10:30
    AWST: 8 * 60,    // +08:00
    UTC: 0,
  };
  const offset = TZ_OFFSETS[timezone] || TZ_OFFSETS.AEST;
  const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;
  return new Date(utcTime + offset * 60000);
}

function getDayOfWeek(date) {
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return days[date.getDay()];
}

function formatTimeHHMM(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function formatDateYYYYMMDD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function timeInRange(timeStr, timeRange) {
  if (!timeRange || !timeRange.start || !timeRange.end) return false;
  const [h, min] = timeStr.split(':').map(Number);
  const time = h * 60 + min;
  const [sh, smin] = timeRange.start.split(':').map(Number);
  const start = sh * 60 + smin;
  const [eh, emin] = timeRange.end.split(':').map(Number);
  const end = eh * 60 + emin;

  // Handle cross-midnight (e.g., 22:00-06:00)
  if (start >= end) {
    return time >= start || time < end;
  }
  return time >= start && time < end;
}

const pauseRules = {
  evaluatePauseState,
  getPauseRules,
  setPauseRules,
  addRecurringRule,
  deleteRecurringRule,
  setManualPause,
};

export default pauseRules;
